import { useEffect, useState } from "react";
import { TableCell, TableRow } from "../ui/table";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import ServiceForm from "../services/serviceForm";
import { Trash } from "lucide-react";
import { getAllServices } from "@/api/services/get-Services";
import { useQuery } from "@tanstack/react-query";
import { useSelectedState } from "../buildings/buildingStates";

export interface Row {
  id: number; // Unique identifier for the row
  service: string; // Service name
  quantity: number | undefined; // Quantity
  price: number | undefined; // Price
}

interface InvoiceRowProps {
  row: Row; // Row object
  totalRows: number;
  onChange: (id: number, field: keyof Row, value: string | number) => void;
  onDelete: (id: number) => void;
}

export default function InvoiceRow({
  row,
  onChange,
  onDelete,
  totalRows,
}: InvoiceRowProps) {
  const [selectedValue, setSelectedValue] = useState(row.service);
  const { selectedBuilding } = useSelectedState();

  const [isLocalAddServiceOpen, setIsLocalAddServiceOpen] = useState(false);
  const { data: services } = useQuery({
    queryKey: ["services", selectedBuilding?.id],
    queryFn: () => getAllServices(selectedBuilding?.id),
  });

  useEffect(() => {
    setSelectedValue(row.service);
  }, [row.service]);

  return (
    <TableRow>
      <TableCell className="p-2 text-zinc-900 sm:text-sm">{row.id}</TableCell>
      <TableCell className="p-2">
        <div className="flex w-full gap-x-2 rounded-md border border-input p-1 text-black shadow-sm outline-1">
          <Select
            value={selectedValue}
            onValueChange={(value) => {
              if (value === "addNewService") {
                setIsLocalAddServiceOpen(true);
                return;
              }
              setSelectedValue(value);
              // Handle service selection immediately
              const service = services?.services.find(
                (s) => s.serviceName === value,
              );
              if (service) {
                onChange(row.id, "service", service.serviceName);
                onChange(row.id, "quantity", service.serviceQuantity ?? "");
                onChange(row.id, "price", service.price ?? "");
              }
            }}
          >
            <SelectTrigger className="w-1/12 min-w-10 rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm" />
            <SelectContent className="min-w-[202px] overflow-y-auto">
              <SelectItem value="addNewService">
                <span className="flex text-blue-600">Add New</span>
              </SelectItem>
              {services?.services.map((service) => (
                <SelectItem
                  key={service.serviceName}
                  value={service.serviceName}
                >
                  {service.serviceName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            className="min-w-[150px] flex-1 shadow-sm"
            value={row.service}
            onChange={(e) => {
              onChange(row.id, "service", e.target.value);
              setSelectedValue("");
            }}
          />
          <Dialog
            open={isLocalAddServiceOpen}
            onOpenChange={setIsLocalAddServiceOpen}
          >
            <DialogContent className="border-transparent bg-inherit">
              <DialogHeader>
                <DialogTitle className="text-black">
                  Register New Service
                </DialogTitle>
                <DialogDescription>
                  Fill in to register a new service
                </DialogDescription>
              </DialogHeader>
              <ServiceForm
                closeDialog={() => setIsLocalAddServiceOpen(false)}
              />
            </DialogContent>
          </Dialog>
        </div>
      </TableCell>
      <TableCell className="p-2">
        <Input
          className="w-full border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
          type="number"
          min={0}
          value={row.quantity ?? ""}
          onChange={(e) => {
            onChange(row.id, "quantity", parseInt(e.target.value, 10));
            setSelectedValue("");
          }}
        />
      </TableCell>
      <TableCell className="p-2">
        <Input
          type="number"
          min={0}
          value={row.price ?? ""}
          onChange={(e) => {
            onChange(row.id, "price", parseFloat(e.target.value));
            setSelectedValue("");
          }}
          className="w-full border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
        />
      </TableCell>
      <TableCell className="p-2">
        <Button
          className="hover:bg-trasnparent bg-transparent"
          onClick={() => onDelete(row.id)}
        >
          <Trash
            className={`h-6 w-6 ${totalRows <= 1 ? "text-gray-300" : "text-red-500 hover:text-red-600"}`}
          />
        </Button>
      </TableCell>
    </TableRow>
  );
}
